'use client';

import { Alert, Box, lighten, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiImage } from '@/components/Utility/StrapiImage';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksHighlightBlock,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

export function HighlightBlock({ block }: { block: ComponentBlocksHighlightBlock }) {
  const theme = useTheme();
  const color = useMemo(() => {
    switch (block.HighlightVariant) {
      case 'Primary':
        return theme.palette.purple[600];
      case 'Secondary':
        return theme.palette.brand.secondary;
      default:
        return theme.palette.brand.brand;
    }
  }, [block.HighlightVariant, theme.palette]);

  return (
    <CustomBackground
      backgroundOptions={(block?.HighlightBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (block?.HighlightDisplayOptions ?? {
          ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'sm',
        }) as StrapiDisplayOptions | undefined
      }
      boxProps={{
        sx: {
          'data-component': 'TableWidget',
        },
      }}
    >
      <Box className={`highlight-${block.HighlightVariant}`}>
        <Alert
          color="info"
          icon={null}
          sx={{
            borderRadius: 4,
            my: 2,
            '&.MuiPaper-root.MuiAlert-root': {
              position: 'relative',
              background: `${lighten(color, 0.75)} !important`,
              overflow: 'hidden',
              color: theme.palette.common.black,
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '0.75rem',
                height: '100%',
                backgroundColor: color,
              },
            },
            '.MuiAlert-icon': {
              display: 'none',
            },
            '.MuiAlert-message': {
              display: 'flex',
              gap: 8,
              flexDirection: block?.AlignImageRight === true ? 'row' : 'row-reverse',
              alignItems: 'center',
              px: 4,
              py: 3,
              '.highlight-img': {
                mx: 2,
              },
            },
          }}
        >
          {block.BackgroundImage && (
            <StrapiImage
              image={block.BackgroundImage}
              size="large"
              width={350}
              className="highlight-img"
            />
          )}
          <Box>
            <GlobalBlockRenderer content={block.Text || []} listBgColor={'white'} />
            {block?.QuoteAuthor && (
              <Typography
                sx={{
                  color: color,
                  fontWeight: 700,
                  fontSize: theme.typography.medium.fontSize,
                  mt: 2,
                }}
              >
                <em>- {block.QuoteAuthor}</em>
              </Typography>
            )}
          </Box>
        </Alert>
      </Box>
    </CustomBackground>
  );
}
